import AWS_ACCOUNTS from '@amzn/aws-gsaas-accounts-helper/dist/configuration/aws_gsaas_aws_accounts_config/aws_accounts.json';
import type { AccountsConfig } from '@amzn/aws-gsaas-accounts-helper/dist/account-helper-browser';
import { AccountHelperBrowser } from '@amzn/aws-gsaas-accounts-helper/dist/account-helper-browser';
import type { AntennaSystemAwsAccount } from '../clients/MgmtCellClientManager';
import { isDefined } from '@amzn/aws-gsaas-common-typescript';
import { uniqBy } from 'lodash';
import type { AntennaCapability, AntennaSystem } from '@amzn/aws-gsaas-accounts-helper';

export const accountsHelper = new AccountHelperBrowser(AWS_ACCOUNTS as unknown as AccountsConfig);

export const GSOC_NAME_PART = 'opsconsole';
export const ANALYTICS_NAME_PART = 'analytics';
export const AGENT_DATA_PRODUCT_NAME_PART = 'agentdataproduct';
export const CANARY_NAME_PART = 'canary';
export const CONTROL_PLANE_GLOBAL_NAME_PART = 'controlplaneddb';
export const CONTROL_PLANE_NAME_PART = 'controlplane';

export enum ACCOUNT_TYPE {
  Internal = 'INTERNAL',
  External = 'EXTERNAL',
  Canary = 'CANARY',
}

export const INTERNAL_ACCOUNT = 'INTERNAL';
export const EXTERNAL_ACCOUNT = 'EXTERNAL';
export const CANARY_ACCOUNT = 'CANARY';

export function getAccountNamePart(accountId: string): string {
  if (accountsHelper.accountIds.includes(accountId)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return accountsHelper.awsAccounts[accountId]!.name_part;
  } else {
    return ACCOUNT_TYPE.External;
  }
}

export function getAccountName(accountId: string | null): string | null {
  if (accountId === null) return null;
  return accountsHelper.awsAccounts[accountId]?.name ?? null;
}

export function getAccountType(namePart: string): ACCOUNT_TYPE {
  if (namePart === EXTERNAL_ACCOUNT) {
    return ACCOUNT_TYPE.External;
  }
  if (namePart.includes(CANARY_NAME_PART)) {
    return ACCOUNT_TYPE.Canary;
  }
  return ACCOUNT_TYPE.Internal;
}

export function getAntennaSystemAccounts(stage: string): AntennaSystemAwsAccount[] {
  return [...accountsHelper.getAccounts('mgmt', stage), ...accountsHelper.getAccounts('mgmt-das73', stage)]
    .filter(account => account.antenna_systems !== undefined && account.antenna_systems.length > 0)
    .map(
      account =>
        ({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          antenna_system: account.antenna_systems![0],
          ...account,
        }) as AntennaSystemAwsAccount,
    )
    .sort((a, b) => getAntennaName(a, stage).localeCompare(getAntennaName(b, stage)));
}

export function getAntennaToAccountMap(stage: string): { [antennaName: string]: AntennaSystemAwsAccount } {
  return Object.fromEntries(getAntennaSystemAccounts(stage).map(account => [account.antenna_system.id, account]));
}

/** Preprod data is wonky, take this with you */
export function getAntennaName(antannaSystemOrAccount: AntennaSystemAwsAccount | AntennaSystem, stage: string): string {
  if ('antenna_system' in antannaSystemOrAccount) {
    return stage === 'prod' ? antannaSystemOrAccount.antenna_system.id : antannaSystemOrAccount.antenna_system.alt_id;
  } else {
    return stage === 'prod' ? antannaSystemOrAccount.id : antannaSystemOrAccount.alt_id;
  }
}

export function getGroundstationToAntennaMap(stage: string) {
  const map: { [siteName: string]: AntennaSystem[] } = {};
  for (const account of getAntennaSystemAccounts(stage)) {
    const groundStation = account.antenna_system.ground_station_id;
    if (groundStation in map) {
      map[groundStation]?.push(account.antenna_system);
    } else {
      map[groundStation] = [account.antenna_system];
    }
  }
  return map;
}

export function getAntennaToGroundstationMap(stage: string) {
  const groundStationToAntennaMap = getGroundstationToAntennaMap(stage);
  const antennaToGroundStationMap: { [antennaName: string]: string } = {};
  for (const [groundStation, antennae] of Object.entries(groundStationToAntennaMap)) {
    for (const antenna of antennae) {
      antennaToGroundStationMap[getAntennaName(antenna, stage)] = groundStation;
    }
  }
  return antennaToGroundStationMap;
}

/**
 * Get the prepass and postpass reservation durations (in seconds) used by the control plane to
 * reserve an antenna for a contact. The control plane uses the max of prepass and max of postpass
 * across all capabilities reserved by the contact. Within a given antenna all capabilities share
 * the same durations, so taking the max across the antenna's capability set is equivalent.
 *
 * Returns 0/0 when durations cannot be determined (e.g. capabilities missing from config).
 */
export function getAntennaReservationDurations(
  antennaName: string,
  stage: string,
): { prepassSeconds: number; postpassSeconds: number } {
  const antennaToAccount = getAntennaToAccountMap(stage);
  // Build a case-insensitive name -> id map. Contact responses sometimes return antenna names
  // in mixed/upper case (e.g. CPT60-ANT1) while the bundled config uses lower case ids.
  const antennaIdsByName: { [name: string]: string } = {};
  for (const account of getAntennaSystemAccounts(stage)) {
    antennaIdsByName[getAntennaName(account, stage).toLowerCase()] = account.antenna_system.id;
  }
  const antennaId = antennaIdsByName[antennaName.toLowerCase()];
  if (antennaId === undefined) return { prepassSeconds: 0, postpassSeconds: 0 };
  const account = antennaToAccount[antennaId];
  if (account === undefined) return { prepassSeconds: 0, postpassSeconds: 0 };
  // capabilities is typed as Record<string, string> upstream but at runtime carries AntennaCapability values
  const capabilities = (account.antenna_system as unknown as { capabilities?: Record<string, AntennaCapability> })
    .capabilities;
  if (capabilities === undefined) return { prepassSeconds: 0, postpassSeconds: 0 };

  let prepassSeconds = 0;
  let postpassSeconds = 0;
  for (const capability of Object.values(capabilities)) {
    if (typeof capability.capabilityPrepassDurationSeconds === 'number') {
      prepassSeconds = Math.max(prepassSeconds, capability.capabilityPrepassDurationSeconds);
    }
    if (typeof capability.capabilityPostpassDurationSeconds === 'number') {
      postpassSeconds = Math.max(postpassSeconds, capability.capabilityPostpassDurationSeconds);
    }
  }
  return { prepassSeconds, postpassSeconds };
}

export function getSites(stage: string): { groundStationId: string; region: string }[] {
  return uniqBy(
    [...accountsHelper.getAccounts('mgmt', stage), ...accountsHelper.getAccounts('mgmt-das73', stage)]
      .filter(account => account.antenna_systems !== undefined && account.antenna_systems.length > 0)
      .flatMap(account =>
        account.antenna_systems?.map(antenna_system => ({
          groundStationId: antenna_system.ground_station_id,
          region: account.region,
        })),
      )
      .filter(isDefined)
      .sort((a, b) => a.groundStationId.localeCompare(b.groundStationId)),
    gs => gs.groundStationId,
  );
}

/**
 * Returns the set of production ground station names derived from the bundled
 * static accounts config (the same source used by getGroundstationToAntennaMap).
 *
 * This is the canonical way to determine which ground stations are production —
 * it avoids hardcoding names and stays in sync with the config automatically.
 */
export function getProductionGroundStationNames(): ReadonlySet<string> {
  return new Set(Object.keys(getGroundstationToAntennaMap('prod')));
}
